import { onBeforeUnmount, ref, watch, type Ref } from 'vue'
import type {
  FormFieldMessage,
  FormFieldMessagesAsyncHandler
} from './form-field-messages-types'

/**
 * Drives the `messagesAsync` behavior inside a `Form*Field`. Mirrors the
 * ergonomics of tanstack's `onChangeAsync` + `onChangeAsyncDebounceMs`:
 * watches a reactive value source, debounces, aborts in-flight calls on
 * re-trigger, and exposes a loading flag + current messages.
 *
 * Each field component calls this once; the caller doesn't need to manage
 * timers, AbortControllers, or cleanup.
 */
export interface UseAsyncMessagesReturn {
  messages: Ref<FormFieldMessage[]>
  isLoading: Ref<boolean>
  /** Manually clear the async messages and cancel any in-flight call. */
  clear: () => void
}

export function useAsyncMessages(options: {
  valueSource: () => string
  handler: Ref<FormFieldMessagesAsyncHandler | undefined>
  debounceMs: Ref<number>
  enabled: Ref<boolean>
}): UseAsyncMessagesReturn {
  const messages = ref<FormFieldMessage[]>([])
  const isLoading = ref(false)

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let currentController: AbortController | null = null

  function cancelPending() {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    if (currentController) {
      currentController.abort()
      currentController = null
    }
  }

  async function runHandler(value: string) {
    const fn = options.handler.value
    if (!fn) return
    if (currentController) currentController.abort()
    currentController = new AbortController()
    const signal = currentController.signal
    isLoading.value = true
    try {
      const result = await fn({ value, signal })
      if (signal.aborted) return
      messages.value = result ?? []
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }
      // On unexpected failure we just clear — the error is the caller's
      // concern (e.g. they can surface it via `:messages` separately).
      messages.value = []

      console.warn('[useAsyncMessages] handler threw:', err)
    } finally {
      if (!signal.aborted) isLoading.value = false
    }
  }

  watch(
    options.valueSource,
    (newValue) => {
      if (!options.enabled.value || !options.handler.value) {
        cancelPending()
        messages.value = []
        isLoading.value = false
        return
      }
      cancelPending()
      debounceTimer = setTimeout(() => {
        debounceTimer = null
        runHandler(newValue)
      }, options.debounceMs.value)
    },
    { immediate: false }
  )

  onBeforeUnmount(() => {
    cancelPending()
  })

  return {
    messages,
    isLoading,
    /** Manually clear the async messages and cancel any in-flight call. */
    clear() {
      cancelPending()
      messages.value = []
      isLoading.value = false
    }
  }
}
